import { ConstantsResponseBoxDto, DocumentDTO, DocPersonaType, Data, Taker, Insured, Associate } from "./required-doc.dto";

export function createDefaultDocument() {
  return {
    docType: '',
    description: '',
    file: undefined,
    associatedTo: '',
  };
};

export function getMandatoryDocs(documentTypes: ConstantsResponseBoxDto[]) {
  return documentTypes.filter((document) => document.obligatorio.length > 0);
}

export function getGroupedDocs(documentTypes: ConstantsResponseBoxDto[]) {
  return documentTypes.filter((document) => !!document.grouped);
}

export function getMissingDocuments(
  mandatoryDocs: ConstantsResponseBoxDto[],
  data: Data,
  uploadedDocuments: DocumentDTO[],
  docPersonaTypes: DocPersonaType,
  taker: Taker,
  groupedDocs: ConstantsResponseBoxDto[],
): { hasAllMandatoryDocs: boolean, hasAllGroupedDocs: boolean, missingDocuments: string[], missingGroupedDocs: string[] } {
  const missingDocuments: string[] = [];
  const missingGroupedDocs: string[] = [];
  let hasAllMandatoryDocs = true;
  let hasAllGroupedDocs = true;

  for (const doc of mandatoryDocs) {
    const requiredDocumentsArray = doc.obligatorio.split("-");

    if (requiredDocumentsArray.includes(docPersonaTypes.TOMADOR.value)) {             // Tomador
      const hasDocument = uploadedDocuments.some(
        (uploadedDoc) =>
          (uploadedDoc.associatedTo === taker.docNumber || uploadedDoc.associatedTo === "-1") &&
          uploadedDoc.type === doc.value
      );
      if (!hasDocument) {
        hasAllMandatoryDocs = false;
        missingDocuments.push(`${doc.label} para <strong>Tomador</strong>.`);
      }
    }

    if (docPersonaTypes.EMPRESA && requiredDocumentsArray.includes(docPersonaTypes.EMPRESA.value)) {             // Empresa (caso especial de TOMADOR)
      const hasDocument = uploadedDocuments.some(
        (uploadedDoc) =>
          (uploadedDoc.associatedTo === taker.docNumber || uploadedDoc.associatedTo === "-1") &&
          uploadedDoc.type === doc.value
      );
      if (!hasDocument) {
        hasAllMandatoryDocs = false;
        missingDocuments.push(`${doc.label} para <strong>Empresa</strong>.`);
      }
    }

    if (requiredDocumentsArray.includes(docPersonaTypes.ASEGURADO.value)) {           // Asegurado
      const hasDocument = checkInsuredDocuments(doc, data, uploadedDocuments);
      if (!hasDocument) {
        hasAllMandatoryDocs = false;
        missingDocuments.push(`${doc.label} para <strong>Todos los Asegurados</strong>.`);
      }
    }

    if (requiredDocumentsArray.includes(docPersonaTypes.ASEGURADO_ESTUDIANTE_EXTRANJERO.value)) { // Asegurado estudiante
      const hasDocument = checkStudentDocuments(doc, data, uploadedDocuments);
      if (!hasDocument) {
        hasAllMandatoryDocs = false;
        missingDocuments.push(`${doc.label} para <strong>Todos los Asegurados que van a realizar la formación</strong>.`);
      }
    }

    // Excepción a la regla: se pide sólo 1 documento para el grupo de asegurados
    if (requiredDocumentsArray.includes(`${docPersonaTypes.ASEGURADO.value}1`)) {
      const hasDocument = uploadedDocuments.some((uploadedDoc) => uploadedDoc.type === doc.value);
      if (!hasDocument) {
        hasAllMandatoryDocs = false;
        missingDocuments.push(`${doc.label} para <strong>uno de los Asegurados</strong>.`);
      }
    }
  }

  if (groupedDocs.length > 0 && uploadedDocuments.length > 0) {
    const { missingTypes, missingValues } = checkGroupedDocs(groupedDocs, data, uploadedDocuments);
    if (missingTypes.length > 0) {
      hasAllGroupedDocs = false;
      missingDocuments.push(...missingTypes);
      missingGroupedDocs.push(...missingValues);
    }
  }

  return {
    missingDocuments,
    missingGroupedDocs,
    hasAllMandatoryDocs,
    hasAllGroupedDocs,
  };
}

function checkGroupedDocs(
  groupedDocs: ConstantsResponseBoxDto[],
  data: Data,
  uploadedDocuments: DocumentDTO[]
) {
  const groupedTypes: { value: string, label: string }[] = [];
  const groupedValues: string[] = [];

  groupedDocs.forEach((doc) => {
    groupedTypes.push({ value: doc.value, label: doc.label });
    groupedValues.push(doc.value);
  });

  const associatedDict: { [key: string]: string[] } = {};

  uploadedDocuments.forEach((doc) => {
    const { associatedTo, type } = doc;
    if (!groupedValues.includes(type) || Number(associatedTo) < 0) return;
    if (!associatedDict[associatedTo]) {
      associatedDict[associatedTo] = [];
    }
    associatedDict[associatedTo].push(type);
  });

  const missingTypes: string[] = [];
  const missingValues: string[] = [];

  Object.entries(associatedDict).forEach(([associatedTo, types]) => {
    groupedTypes.forEach(({ value, label }) => {
      if (!types.includes(value)) {
        missingValues.push(value);
        missingTypes.push(
          `${label} para el asegurado <strong>${data.insureds[Number(associatedTo)].name} ${
            data.insureds[Number(associatedTo)].lastname1
          }</strong>. (${data.insureds[Number(associatedTo)].docNumber}).`
        );
      }
    });
  });
  
  return {
    hasGroupedDocs: missingTypes.length > 0,
    missingTypes,
    missingValues
  };
}

function checkInsuredDocuments(
  mandatoryDoc: ConstantsResponseBoxDto,
  data: Data,
  uploadedDocuments: DocumentDTO[]
): boolean {
  let result = true;
  for (let i = 0; i < data.insureds.length; i++) {
    const documents = getInsuredDocuments(data.insureds[i], i, uploadedDocuments, mandatoryDoc);

    if (documents.length === 0) {
      result = false;
      break;
    }
  }
  return result;
}

function checkStudentDocuments(
  mandatoryDoc: ConstantsResponseBoxDto,
  data: Data,
  uploadedDocuments: DocumentDTO[]
): boolean {
  let result = true;
  for (let i = 0; i < data.insureds.length; i++) {
    const isStudent = data.insureds[i].isStudent;
    if (isStudent) {
      const documents = getInsuredDocuments(data.insureds[i], i, uploadedDocuments, mandatoryDoc)

      if (documents.length === 0) {
        result = false;
        break;
      }
    }
  }
  return result;
}

function getInsuredDocuments (insured: Insured,index: number, uploadedDocuments: DocumentDTO[], mandatoryDoc: ConstantsResponseBoxDto) {
  const { sameAsTaker } = insured;
  const documents = uploadedDocuments.filter((doc) => {
    if (doc.associatedTo === index.toString() && doc.type === mandatoryDoc.value) return true;
    if (sameAsTaker && doc.associatedTo === "-1" && doc.type === mandatoryDoc.value) return true;
    return false;
  });
  return documents;
}


export function getAssociates(insureds: Insured[]) : Associate[] {
    const associates: Associate[] = [
      {
        index: -1,
        name: "",
        document: "-1",
        title: "Tomador/Póliza",
      },
    ];

    if (insureds && insureds.length) {
      insureds.forEach((insured, index) => {
        associates.push({
          index,
          name: insured.name,
          document: insured.docNumber,
          title: `${insured.name} ${insured.lastname1} (${insured.docNumber})`,
          isStudent: insured.isStudent,
        });
      });
    }
    return associates;
  }